import {
  Autocomplete,
  TextField,
  type AutocompleteRenderInputParams,
} from "@mui/material";
import { useCallback, type SyntheticEvent } from "react";
import { useOutboundBenchCompanyUsersQuery } from "../../../generated/graphql";
import { companyUserLabel, type BenchCompanyUserOption } from "../bench-run";

const EMPTY_USERS: BenchCompanyUserOption[] = [];

const HELPER_TEXT =
  "Optional. The TMS records an order as received at origin under one of this company's users. Pick anyone here if your orders travel on a line-haul manifest; otherwise leave it empty.";

const isSameUser = (
  option: BenchCompanyUserOption,
  value: BenchCompanyUserOption,
) => option.uuid === value.uuid;

type BenchUserPickerProps = {
  readonly companyUuid: string | null;
  readonly value: BenchCompanyUserOption | null;
  readonly onChange: (value: BenchCompanyUserOption | null) => void;
};

/** Picks the user of the partnership's company that a run acts as. */
const BenchUserPicker = ({
  companyUuid,
  value,
  onChange,
}: BenchUserPickerProps) => {
  // The internal app's watch queries default to `errorPolicy: "ignore"`,
  // which would show a failed load as a company with no users.
  const { data, loading, error } = useOutboundBenchCompanyUsersQuery({
    errorPolicy: "none",
    skip: companyUuid === null,
    variables: { companyUuid: companyUuid ?? "" },
  });

  const handleChange = useCallback(
    (_: SyntheticEvent, next: BenchCompanyUserOption | null) => {
      onChange(next);
    },
    [onChange],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={error !== undefined}
        helperText={
          error === undefined
            ? HELPER_TEXT
            : `Could not load this company's users: ${error.message}`
        }
        label="Act as user"
      />
    ),
    [error],
  );

  return (
    <Autocomplete
      disabled={companyUuid === null}
      getOptionLabel={companyUserLabel}
      isOptionEqualToValue={isSameUser}
      loading={loading}
      loadingText="Loading users…"
      noOptionsText="This company has no active users."
      options={data?.outboundBenchCompanyUsers ?? EMPTY_USERS}
      renderInput={renderInput}
      value={value}
      onChange={handleChange}
    />
  );
};

export default BenchUserPicker;
